/**
 * Family context resolver.
 *
 * The app's domain entities (tasks, events, albums, …) are scoped to a
 * `family_id` and, for notifications, a `profile_id`. Neither value lives on
 * the client {@link import('@/features/auth/types/auth.types').User} object, so
 * the Supabase service implementations resolve them here.
 *
 * Multi-family model (007): the scope is the user's ACTIVE family
 * (`profiles.active_family_id`). Resolution order:
 *   1. the in-session override set by the family store (updated on family
 *      switch — cheapest path, no round-trip), else
 *   2. a one-time `profiles.active_family_id` lookup keyed by the
 *      authenticated user id, cached per user — and, while it is on the wire,
 *      shared by every concurrent caller that missed both (see
 *      `inFlightFamilyLookup`; six realtime scopes resolve in one commit at
 *      boot, before the override exists).
 *
 * The cache/override are invalidated on sign-out / user change / family switch
 * via {@link clearFamilyContextCache} and {@link setActiveFamilyOverride}.
 *
 * Resolution reaches Supabase only through {@link getSupabaseClient}, which is
 * lazy, so importing this module opens no connection — the client is created on
 * the first resolver call that misses the override and the cache.
 *
 * @module shared/lib/familyContext
 */

import { getSupabaseClient } from '@/shared/clients/supabaseClient';

let cachedFamilyId: string | null = null;
let cachedForUserId: string | null = null;
let activeFamilyOverride: string | null = null;

/**
 * The `profiles.active_family_id` lookup that is currently on the wire, so N
 * concurrent cache-and-override misses share ONE round trip instead of issuing
 * N identical selects.
 *
 * This is the cold authenticated boot, not an edge case. `MainLayout` is the
 * element of the `/` route and `FamilyGate` is a child rendered into its
 * `<Outlet/>`, so five {@link import('@/shared/hooks/useRealtimeScope')}
 * instances (presence, tasks, events, albums, notifications) — six with
 * `LovePage`'s appreciations, which sits outside the gate entirely — mount and
 * resolve in the same commit, while `FamilyGate` is still painting its loading
 * screen and `familyStore.hydrate` is one to two round trips away from calling
 * {@link setActiveFamilyOverride}. Every one of them missed the override and
 * the cache, which is written only AFTER the await.
 *
 * Keyed by user id so a lookup started for a signing-out user can never be
 * handed to the next one, and cleared by {@link setActiveFamilyOverride} and
 * {@link clearFamilyContextCache} for the same reason. A rejected lookup is
 * shared too — the concurrent callers would all have failed — and clearing it
 * on settle means the next call retries rather than replaying the failure.
 */
let inFlightFamilyLookup: {
  readonly userId: string;
  readonly promise: Promise<string>;
} | null = null;

type ActiveFamilyListener = (familyId: string | null) => void;
const activeFamilyListeners = new Set<ActiveFamilyListener>();

/**
 * Subscribe to active-family changes (family switch / sign-out). Returns an
 * unsubscribe function. Used by realtime scoping to re-resolve subscriptions.
 */
export function subscribeActiveFamilyChange(
  listener: ActiveFamilyListener
): () => void {
  activeFamilyListeners.add(listener);
  return () => {
    activeFamilyListeners.delete(listener);
  };
}

function notifyActiveFamilyChange(familyId: string | null): void {
  for (const listener of activeFamilyListeners) {
    try {
      listener(familyId);
    } catch (error) {
      console.error('Active family listener failed:', error);
    }
  }
}

/**
 * Resolve the current authenticated profile id (equal to the Supabase auth
 * user id, which is the primary key of `public.profiles`).
 *
 * READS THE LOCAL SESSION, NOT `auth.getUser()`. `getUser()` issues
 * `GET /auth/v1/user` on every call (auth-js `_getUser` always makes the
 * request when no jwt is passed), and this resolver sits above the offline gate
 * of every service write — `createTask`, `createEvent` and the realtime scope
 * resolver all await it first. A dead uplink therefore rejected here, above the
 * transport-failure fallback that exists precisely for that case, and the write
 * was lost rather than queued. `getSession()` reads the persisted session and
 * refreshes it only when it has expired, so a network blip costs nothing.
 *
 * Nothing is weakened by the swap: the id is the same `auth.uid()` either way,
 * it is only ever used to build filters and payloads, and the enforcement
 * boundary is Row Level Security on the server — a locally forged session
 * carries a JWT the server rejects.
 *
 * @throws Error when there is no active Supabase session.
 */
export async function getCurrentProfileId(): Promise<string> {
  const { data, error } = await getSupabaseClient().auth.getSession();
  if (error || !data.session) {
    throw new Error(
      'Няма активна сесия: не може да се определи текущият потребител.'
    );
  }
  return data.session.user.id;
}

/**
 * Resolve the current user's ACTIVE `family_id`.
 *
 * The override set by the family store on hydration is the normal path and
 * costs nothing. The fallback resolves the user from the LOCAL session for the
 * same reason {@link getCurrentProfileId} does — `family_id` is part of every
 * insert payload, so this necessarily runs above the offline gate in
 * `createTask`, `createEvent`, `createAlbum` and `addPhoto`, and a network
 * round trip there loses the write instead of queueing it. The `profiles`
 * lookup below still needs the wire, but only on the cache-and-override miss.
 *
 * @throws Error when there is no active session or the user has no active
 *   family (callers are behind the family onboarding gate, so this is
 *   exceptional).
 */
export async function getCurrentFamilyId(): Promise<string> {
  if (activeFamilyOverride !== null) {
    return activeFamilyOverride;
  }

  const client = getSupabaseClient();
  const { data: sessionData, error: sessionError } =
    await client.auth.getSession();
  if (sessionError || !sessionData.session) {
    throw new Error(
      'Няма активна сесия: не може да се определи семейството на потребителя.'
    );
  }

  const userId = sessionData.session.user.id;
  if (cachedFamilyId !== null && cachedForUserId === userId) {
    return cachedFamilyId;
  }

  if (inFlightFamilyLookup !== null && inFlightFamilyLookup.userId === userId) {
    return inFlightFamilyLookup.promise;
  }

  const promise = fetchActiveFamilyId(userId);
  const entry = { userId, promise };
  inFlightFamilyLookup = entry;
  // Settled either way: drop the memo so the next caller starts fresh, but
  // only if a reset (sign-out, family switch) has not already replaced it.
  void promise.then(
    () => {
      if (inFlightFamilyLookup === entry) {
        inFlightFamilyLookup = null;
      }
    },
    () => {
      if (inFlightFamilyLookup === entry) {
        inFlightFamilyLookup = null;
      }
    }
  );
  return promise;
}

/**
 * The one uncached `profiles.active_family_id` read, split out so
 * {@link getCurrentFamilyId} can memoise it while it is on the wire.
 *
 * The three failure modes are told apart deliberately. Collapsing them under
 * one message reported a dead uplink as "липсва профил", which sent whoever
 * read it looking for a missing row — and this resolver runs above the offline
 * gate of `createTask`, `createEvent`, `createAlbum` and `addPhoto`, so a
 * transport failure here is the likeliest one of the three by far.
 */
async function fetchActiveFamilyId(userId: string): Promise<string> {
  const { data, error } = await getSupabaseClient()
    .from('profiles')
    .select('active_family_id')
    .eq('id', userId)
    .single();
  if (error) {
    throw new Error(
      `Семейството на потребителя не може да бъде определено: ${error.message}`
    );
  }
  if (!data) {
    throw new Error(
      'Семейството на потребителя не може да бъде определено (липсва профил).'
    );
  }
  if (data.active_family_id === null) {
    throw new Error(
      'Потребителят няма активно семейство. Създайте или се присъединете към семейство.'
    );
  }

  cachedFamilyId = data.active_family_id;
  cachedForUserId = userId;
  return data.active_family_id;
}

/**
 * Set (or clear with `null`) the in-session active family override. Called by
 * the family store on hydration and on every family switch so subsequent
 * service calls scope to the new family without a round-trip.
 */
export function setActiveFamilyOverride(familyId: string | null): void {
  activeFamilyOverride = familyId;
  cachedFamilyId = null;
  cachedForUserId = null;
  // A lookup still on the wire was started before this override existed, so
  // it may be resolving the family the user has just left. Callers already
  // waiting on it keep their answer (same as the cache behaved before the
  // memo); nobody NEW may join it.
  inFlightFamilyLookup = null;
  notifyActiveFamilyChange(familyId);
}

/**
 * Invalidate the cached `family_id` and the override. Call on sign-out or
 * user switch.
 */
export function clearFamilyContextCache(): void {
  cachedFamilyId = null;
  cachedForUserId = null;
  activeFamilyOverride = null;
  // Security, not just freshness: on sign-out the in-flight lookup belongs to
  // the outgoing user, and the next one must never be handed it.
  inFlightFamilyLookup = null;
  notifyActiveFamilyChange(null);
}
